/**
 * Frontend Event Queue for UI Synchronization
 * Replaces nested setTimeout calls with proper event queuing and cancellation
 */

interface QueuedUIEvent {
  id: string;
  action: string;
  delay: number;
  data?: any;
  timestamp: number;
  timeoutId?: ReturnType<typeof setTimeout>;
  status: 'pending' | 'executing' | 'completed' | 'cancelled';
  callback?: () => void;
}

export class UIEventQueue {
  private queue: QueuedUIEvent[] = [];
  private activeEvents = new Map<string, QueuedUIEvent>();
  private eventIdCounter = 0;
  private isDestroyed = false;

  constructor() {
    console.log('[UIEventQueue] 🏗️ Initializing new queue instance...');
    
    // Initialize properties explicitly
    this.queue = [];
    this.activeEvents = new Map<string, QueuedUIEvent>();
    this.eventIdCounter = 0;
    this.isDestroyed = false;

    // Bind methods to prevent context issues
    this.enqueue = this.enqueue.bind(this);
    this.cancel = this.cancel.bind(this);
    this.cancelAll = this.cancelAll.bind(this);
    this.cancelByAction = this.cancelByAction.bind(this);
    this.processQueue = this.processQueue.bind(this);
    
    console.log('[UIEventQueue] ✅ Queue instance initialized', {
      queueLength: this.queue.length,
      activeEventsSize: this.activeEvents.size,
      isDestroyed: this.isDestroyed
    });
  }

  /**
   * Add an event to the queue with a delay and callback
   */
  enqueue(action: string, delay: number, callback: () => void, data?: any): string {
    if (this.isDestroyed) {
      console.warn('[UIEventQueue] Cannot enqueue - queue is destroyed');
      return '';
    }

    const eventId = `ui_event_${++this.eventIdCounter}_${Date.now()}`;
    
    const event: QueuedUIEvent = {
      id: eventId,
      action,
      delay,
      data,
      timestamp: Date.now(),
      status: 'pending',
      callback
    };

    this.queue.push(event);
    console.log(`[UIEventQueue] 📋 Enqueued: ${action} (delay: ${delay}ms, id: ${eventId})`);
    
    // Process the queue
    this.processQueue();
    
    return eventId;
  }

  /**
   * Cancel a specific event by ID
   */
  cancel(eventId: string): boolean {
    if (this.isDestroyed) return false;

    // Defensive check for activeEvents
    if (!this.activeEvents) {
      console.warn('[UIEventQueue] activeEvents is undefined in cancel, reinitializing...');
      this.activeEvents = new Map<string, QueuedUIEvent>();
      return false;
    }

    // Check if it's in the active events
    const activeEvent = this.activeEvents.get(eventId);
    if (activeEvent) {
      if (activeEvent.timeoutId) {
        clearTimeout(activeEvent.timeoutId);
      }
      activeEvent.status = 'cancelled';
      this.activeEvents.delete(eventId);
      console.log(`[UIEventQueue] ❌ Cancelled active event: ${eventId}`);
      return true;
    }

    // Check if it's in the queue
    const queueIndex = this.queue.findIndex(event => event.id === eventId);
    if (queueIndex !== -1) {
      this.queue[queueIndex].status = 'cancelled';
      this.queue.splice(queueIndex, 1);
      console.log(`[UIEventQueue] ❌ Cancelled queued event: ${eventId}`);
      return true;
    }

    return false;
  }

  /**
   * Cancel all events matching a specific action
   */
  cancelByAction(action: string): number {
    if (this.isDestroyed) return 0;

    let cancelledCount = 0;

    // Defensive check for activeEvents
    if (!this.activeEvents) {
      console.warn('[UIEventQueue] activeEvents is undefined, reinitializing...');
      this.activeEvents = new Map<string, QueuedUIEvent>();
      return 0;
    }

    // Cancel active events
    for (const [eventId, event] of this.activeEvents) {
      if (event.action === action) {
        this.cancel(eventId);
        cancelledCount++;
      }
    }

    // Cancel queued events
    const indicesToRemove: number[] = [];
    this.queue.forEach((event, index) => {
      if (event.action === action) {
        event.status = 'cancelled';
        indicesToRemove.push(index);
        cancelledCount++;
      }
    });

    // Remove cancelled events from queue (reverse order to maintain indices)
    indicesToRemove.reverse().forEach(index => {
      this.queue.splice(index, 1);
    });

    if (cancelledCount > 0) {
      console.log(`[UIEventQueue] ❌ Cancelled ${cancelledCount} events with action: ${action}`);
    }

    return cancelledCount;
  }

  /**
   * Cancel all pending and active events
   */
  cancelAll(): number {
    if (this.isDestroyed) return 0;

    let cancelledCount = 0;

    // Defensive check for activeEvents
    if (!this.activeEvents) {
      console.warn('[UIEventQueue] activeEvents is undefined in cancelAll, reinitializing...');
      this.activeEvents = new Map<string, QueuedUIEvent>();
    } else {
      // Cancel all active events
      for (const eventId of this.activeEvents.keys()) {
        this.cancel(eventId);
        cancelledCount++;
      }
    }

    // Cancel all queued events
    cancelledCount += this.queue.length;
    this.queue.forEach(event => {
      event.status = 'cancelled';
      if (event.timeoutId) {
        clearTimeout(event.timeoutId);
      }
    });
    this.queue = [];

    console.log(`[UIEventQueue] ❌ Cancelled all events: ${cancelledCount} total`);
    return cancelledCount;
  }

  /**
   * Get queue status for debugging
   */
  getStatus() {
    // Defensive checks for undefined properties
    if (!this.queue) {
      console.warn('[UIEventQueue] this.queue is undefined in getStatus, reinitializing...');
      this.queue = [];
    }
    if (!this.activeEvents) {
      console.warn('[UIEventQueue] this.activeEvents is undefined in getStatus, reinitializing...');
      this.activeEvents = new Map<string, QueuedUIEvent>();
    }

    return {
      queuedEvents: this.queue.length,
      activeEvents: this.activeEvents.size,
      isDestroyed: this.isDestroyed,
      queueDetails: this.queue.map(e => ({
        id: e.id,
        action: e.action,
        delay: e.delay,
        status: e.status,
        waitTime: Date.now() - e.timestamp
      })),
      activeDetails: Array.from(this.activeEvents.values()).map(e => ({
        id: e.id,
        action: e.action,
        status: e.status,
        runTime: Date.now() - e.timestamp
      }))
    };
  }

  /**
   * Process the queue - execute next pending event
   */
  private processQueue(): void {
    if (this.isDestroyed) return;

    // Get the next pending event
    const nextEvent = this.queue.find(event => event.status === 'pending');
    if (!nextEvent) {
      return;
    }

    // Move from queue to active
    const queueIndex = this.queue.indexOf(nextEvent);
    this.queue.splice(queueIndex, 1);
    nextEvent.status = 'executing';
    this.activeEvents.set(nextEvent.id, nextEvent);

    console.log(`[UIEventQueue] ⚡ Executing: ${nextEvent.action} (delay: ${nextEvent.delay}ms, id: ${nextEvent.id})`);

    // Set up the delayed execution
    nextEvent.timeoutId = setTimeout(() => {
      this.executeEvent(nextEvent);
    }, nextEvent.delay);

    // Continue processing queue after a small delay to avoid stack overflow
    setTimeout(() => this.processQueue(), 10);
  }

  /**
   * Execute the event and clean up
   */
  private executeEvent(event: QueuedUIEvent): void {
    if (this.isDestroyed || event.status === 'cancelled') {
      return;
    }

    event.status = 'completed';
    this.activeEvents.delete(event.id);

    console.log(`[UIEventQueue] ✅ Completed: ${event.action} (id: ${event.id})`);

    // Execute the callback
    try {
      if (event.callback) {
        event.callback();
      }
    } catch (error) {
      console.error(`[UIEventQueue] 🚨 Error executing event ${event.id}:`, error);
    }

    // Continue processing queue
    this.processQueue();
  }

  /**
   * Clean up - cancel all events and clear timers
   */
  destroy(): void {
    console.log('[UIEventQueue] 🧹 Destroying queue...');
    this.cancelAll();
    this.queue = [];
    
    // Defensive check before clearing activeEvents
    if (this.activeEvents) {
      this.activeEvents.clear();
    } else {
      console.warn('[UIEventQueue] activeEvents was undefined during destroy');
    }
    
    this.isDestroyed = true;
    console.log('[UIEventQueue] 🧹 Queue destroyed');
  }
}

// Singleton instance for global use
let globalUIQueue: UIEventQueue | null = null;

export function getUIEventQueue(): UIEventQueue {
  if (!globalUIQueue) {
    console.log('[UIEventQueue] 🔧 Creating singleton instance...');
    globalUIQueue = new UIEventQueue();
    console.log('[UIEventQueue] ✅ Singleton instance created');
  } else {
    console.log('[UIEventQueue] 📋 Using existing singleton instance');
  }
  return globalUIQueue;
}

export function resetUIEventQueue(): void {
  if (globalUIQueue) {
    globalUIQueue.destroy();
    globalUIQueue = null;
  }
}

// Hook for React components
export function useUIEventQueue() {
  const queue = getUIEventQueue();
  
  return {
    enqueue: queue.enqueue,
    cancel: queue.cancel,
    cancelByAction: queue.cancelByAction,
    cancelAll: queue.cancelAll,
    getStatus: queue.getStatus,
    destroy: queue.destroy
  };
}